import Modal from "../../../../../../components/base/Modal";
import type { Trip, ThemeClasses, TripAssignment } from "../types";
import { TIMEZONE_OPTIONS } from "../types";
import { getStatusToneForTrip, getStatusLabel, getPriorityTone, getPriorityLabel, getAssignmentStatusTone, formatTripDate } from "../utils";
import { Badge } from "../../../../../../components/ui";
import type { TFunction } from "i18next";
import { useRoutesStore } from "../../../../../../store/admin/routesStore";
import { useUsersStore } from "../../../../../../store/admin/usersStore";
import { useAllDevices } from "../../../../../../features/devices/queries";
import { RouteSummary } from "./RouteSummary";
import { useEffect, useState } from "react";
import { Button } from "../../../../../../components/ui";
import { QuoteDetailModal } from "../../../../../../components/quotes/QuoteDetailModal";
import { formatMoney } from "../../../../../../components/quotes/QuoteTotals";
import { marginFromPrice } from "../../../../../../features/quotes/calc";

interface TripDetailsModalProps {
  trip: Trip;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit: () => void;
  /** Abre el cotizador para este viaje (sólo si la app finanzas-operativas está habilitada). */
  onQuote?: () => void;
  t: TFunction;
  disableCRUD: boolean;
}

export function TripDetailsModal({
  trip,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
  onQuote,
  t,
  disableCRUD,
}: TripDetailsModalProps) {
  const [showQuote, setShowQuote] = useState(false);
  const routes = useRoutesStore((state) => state.routes || []);
  const fetchRoutes = useRoutesStore((state) => state.fetchRoutes);
  const { data: devices = [] } = useAllDevices();
  const { users, fetchUsers } = useUsersStore();

  useEffect(() => {
    if (routes.length === 0) {
      fetchRoutes({ limit: 500, page: 1 });
    }
    if (users.length === 0) {
      fetchUsers({ limit: 500, page: 1 });
    }
  }, [routes.length, users.length, fetchRoutes, fetchUsers]);

  // Get route from store
  const selectedRoute = routes.find((r) => r.id === trip.route_id) || null;

  // Get user info from store
  const getUserInfo = (userIdOrEmail: string) => {
    const user = users.find((u) => u.id === userIdOrEmail || u.email === userIdOrEmail);
    if (user) {
      return {
        name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email,
        email: user.email,
      };
    }
    if (userIdOrEmail.includes("@")) {
      return { name: userIdOrEmail, email: userIdOrEmail };
    }
    return { name: userIdOrEmail, email: "" };
  };

  const formatDateTime = (datetime: string) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleString("es-MX", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric",
      hour: "2-digit", 
      minute: "2-digit" 
    });
  };

  const getDeviceLabel = (assignment: TripAssignment) => {
    // Try from populated device first
    if (assignment.device?.name) return assignment.device.name;
    if (assignment.device?.identifier) return assignment.device.identifier;
    // Fallback to store
    const device = devices.find((d) => d.id === assignment.device_id);
    if (device) {
      const vehicleName = device.vehicle?.name || "";
      const trailerName = device.remolque?.name || "";
      return `${device.name || device.identifier} ${vehicleName ? `(${vehicleName})` : ""} ${trailerName ? `(${trailerName})` : ""}`.trim();
    }
    return assignment.device_id;
  };

  return (
    <Modal
      title={t("trips.modal.detailsTitle")}
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full sm:w-2/4"
      showEditButton
      onEdit={onEdit}
      disableCRUD={disableCRUD}
      content={
        <div className="space-y-4 max-h-[70vh] overflow-y-auto px-2 w-full">
          {/* Status & Priority badges */}
          <div className="flex items-center gap-3">
            {trip.status_id && (
              <Badge tone={getStatusToneForTrip(trip)}>
                {getStatusLabel(trip, t)}
              </Badge>
            )}
            {trip.priority && (
              <Badge tone={getPriorityTone(trip.priority)}>
                {getPriorityLabel(trip.priority, t)}
              </Badge>
            )}
          </div>

          {/* Basic Info Section */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} border-b ${themeClasses.cardBorder} pb-2`}>
            {t("trips.sections.basicInfo")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.fields.title")}</span>
              <p className={`text-sm font-medium ${themeClasses.text}`}>{trip.title || "—"}</p>
            </div>
            <div>
              <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.fields.priority")}</span>
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {trip.priority ? getPriorityLabel(trip.priority, t) : "—"}
              </p>
            </div>
            <div className="sm:col-span-2">
              <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.fields.description")}</span>
              <p className={`text-sm font-medium ${themeClasses.text}`}>{trip.description || "—"}</p>
            </div>
          </div>

          {/* Costos y precio (cotizador de costos operativos) */}
          {(trip.quote_id || onQuote) && (
            <>
              <h3 className={`text-sm font-semibold ${themeClasses.text} border-b ${themeClasses.cardBorder} pb-2 mt-4`}>
                {t("trips.sections.costs")}
              </h3>
              {trip.quote_id ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                  <div>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.fields.estimatedCost")}</span>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>{formatMoney(trip.estimated_cost)}</p>
                  </div>
                  <div>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.fields.price")}</span>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>{formatMoney(trip.price)}</p>
                  </div>
                  <div>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.fields.margin")}</span>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {trip.price ? `${marginFromPrice(trip.estimated_cost ?? 0, trip.price).toFixed(1)} %` : "—"}
                    </p>
                  </div>
                  <div className="flex items-end">
                    <Button variant="outline" size="sm" icon={<i className="ri-file-list-3-line" />} onClick={() => setShowQuote(true)}>
                      {t("trips.actions.viewQuote")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className={`text-sm ${themeClasses.textSecondary} italic`}>{t("trips.fields.noQuote")}</p>
                  <Button variant="soft" size="sm" icon={<i className="ri-calculator-line" />} onClick={onQuote} disabled={disableCRUD}>
                    {t("trips.actions.quoteTrip")}
                  </Button>
                </div>
              )}
            </>
          )}
          {showQuote && trip.quote_id && <QuoteDetailModal quoteId={trip.quote_id} onClose={() => setShowQuote(false)} />}

          {/* Route Info Section */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} border-b ${themeClasses.cardBorder} pb-2 mt-4`}>
            {t("trips.sections.routeInfo")}
          </h3>
          {selectedRoute ? (
            <RouteSummary
              route={selectedRoute}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              t={t}
            />
          ) : (
            <p className={`text-sm ${themeClasses.textSecondary} italic`}>
              {trip.route?.name || t("trips.noRouteAssigned")}
            </p>
          )}

          {/* Assignments Section */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} border-b ${themeClasses.cardBorder} pb-2 mt-4`}>
            {t("trips.assignments.title")} ({trip.assignments?.length || 0})
          </h3>
          {trip.assignments && trip.assignments.length > 0 ? (
            <div className="space-y-2">
              {trip.assignments.map((assignment, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border ${themeClasses.cardBorder} bg-surface`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                      {getDeviceLabel(assignment)}
                    </p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {formatDateTime(assignment.start_time)} - {formatDateTime(assignment.end_time)}
                      {assignment.timezone && (
                        <span className="ml-2 text-primary-500">
                          <i className="ri-time-zone-line mr-0.5" />
                          {TIMEZONE_OPTIONS[assignment.timezone as keyof typeof TIMEZONE_OPTIONS] || assignment.timezone}
                        </span>
                      )}
                    </p>
                  </div>
                  <Badge tone={getAssignmentStatusTone(assignment.status)}>
                    {t(`trips.assignmentStatus.${assignment.status}`)}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-sm ${themeClasses.textSecondary} italic`}>{t("trips.assignments.empty")}</p>
          )}

          {/* Notifications Section */}
          {trip.notifiedUsers && trip.notifiedUsers.length > 0 && (
            <>
              <h3 className={`text-sm font-semibold ${themeClasses.text} border-b ${themeClasses.cardBorder} pb-2 mt-4`}>
                {t("trips.sections.notifications")} ({trip.notifiedUsers.length})
              </h3>
              <div className="space-y-2">
                {trip.notifiedUsers.map((userIdOrEmail, idx) => {
                  const userInfo = getUserInfo(userIdOrEmail);
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${themeClasses.cardBorder} bg-surface`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-primary-500/20`}>
                        <i className="ri-user-line text-primary-500"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                          {userInfo.name}
                        </p>
                        {userInfo.email && userInfo.email !== userInfo.name && (
                          <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
                            {userInfo.email}
                          </p>
                        )}
                      </div>
                      <i className={`ri-mail-check-line ${themeClasses.textSecondary}`}></i>
                    </div>
                  );
                })}
              </div>
              <div
                className={`flex items-center gap-3 p-3 rounded-lg border ${themeClasses.cardBorder} bg-surface`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-primary-500/20`}>
                  <i className="ri-mail-send-line text-primary-500"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${themeClasses.text}`}>
                    {t("trips.fields.sendEmails")}
                  </p>
                </div>
                <Badge tone={trip.sendEmails ? "primary" : "neutral"}>
                  {trip.sendEmails
                    ? t("trips.fields.sendEmailsOn")
                    : t("trips.fields.sendEmailsOff")}
                </Badge>
              </div>
            </>
          )}

          {/* Comment Section */}
          {trip.comment && (
            <>
              <h3 className={`text-sm font-semibold ${themeClasses.text} border-b ${themeClasses.cardBorder} pb-2 mt-4`}>
                {t("trips.fields.comment")}
              </h3>
              <p className={`text-sm ${themeClasses.text} bg-surface rounded-lg p-3`}>
                {trip.comment}
              </p>
            </>
          )}

          {/* Dates Section */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} border-b ${themeClasses.cardBorder} pb-2 mt-4`}>
            {t("trips.sections.dates")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.fields.createdAt")}</span>
              <p className={`text-sm font-medium ${themeClasses.text}`}>{formatTripDate(trip.createdAt)}</p>
            </div>
            <div>
              <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.fields.updatedAt")}</span>
              <p className={`text-sm font-medium ${themeClasses.text}`}>{formatTripDate(trip.updatedAt)}</p>
            </div>
          </div>
        </div>
      }
    />
  );
}
